import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * Pipeline → workspace handoff payload.
 *
 * Used when a bucket bar on `/client-admin/pipeline` is clicked. Instead of
 * encoding the filter scope as URL query params (UUIDs leak, links are
 * fragile, and the URL drifts out of sync with drawer edits), we write the
 * scope to this sessionStorage-backed store and navigate to a bare destination
 * path. The destination page reads + clears the payload on mount.
 *
 * Refresh semantics: once consumed, the payload is cleared. The destination
 * page then relies on the existing `queue-context.store` / `history-context`
 * snapshots to survive page reloads. A fresh tab gets a clean default state.
 */
export type PipelineHandoffBucket =
  | 'received'
  | 'copilot'
  | 'autopilot'
  | 'query'
  | 'coding_complete'
  | 'exceptions'

export type PipelineHandoffTab = 'exported' | 'archived'

export interface PipelineHandoffFilters {
  client_id?: string[]
  facility_id?: string[]
  provider_id?: string[]
  specialty?: string[]
  /** Pipeline's date range filters on encounter.created_at → maps to queue's "Received On" semantic. */
  received_from?: string
  received_to?: string
}

export interface PipelineHandoffPayload {
  destination: 'queue' | 'history'
  /** Bucket tab to select on /workspace/queue. Unset when destination='history'. */
  bucket?: PipelineHandoffBucket
  /** Tab to select on /workspace/history. Unset when destination='queue'. */
  tab?: PipelineHandoffTab
  filters: PipelineHandoffFilters
}

interface PipelineHandoffState {
  payload: PipelineHandoffPayload | null
  setPayload: (p: PipelineHandoffPayload) => void
  /**
   * Returns the current payload and clears it from the store. Read-once
   * semantic so back-button / re-mount doesn't replay the same filter
   * handoff over the user's subsequent edits.
   */
  consume: () => PipelineHandoffPayload | null
  clear: () => void
}

export const usePipelineHandoffStore = create<PipelineHandoffState>()(
  persist(
    (set, get) => ({
      payload: null,
      setPayload: (p) => set({ payload: p }),
      consume: () => {
        const current = get().payload
        if (current) set({ payload: null })
        return current
      },
      clear: () => set({ payload: null }),
    }),
    {
      name: 'pipeline-handoff',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ payload: state.payload }),
    },
  ),
)
